// The 3D view's section plane (docs/claude/rendering.md, *The 3D section*): the
// plane a new section is laid on — the surface under the pointer, or one of the
// orthogonal planes —, which side of it the view keeps, the figure its handle and
// its panel row set, and the outline it is drawn with. Pure: the viewer clips by
// it, main.ts runs the tool.

import type { Section3d } from '../shared/types';
import type {
  Handle,
  Pt3,
} from './handles';

/** How a section lies: parallel to a surface, or on one of the orthogonal planes
 *  (AutoCAD's XY / YZ / ZX: horizontal, square to the X axis, square to Y). */
export type Section3dPlane = 'surface' | 'xy' | 'yz' | 'zx';
export type OrthoPlane = Exclude<Section3dPlane, 'surface'>;

/** The axis square to each orthogonal plane. */
const AXIS: Record<OrthoPlane, Pt3> = { xy: { x: 0, y: 0, z: 1 }, yz: { x: 1, y: 0, z: 0 }, zx: { x: 0, y: 1, z: 0 } };

/** The plane is laid this far toward the viewer (mm), so a face it lies on is
 *  kept whole — exactly on it, each pixel of the face would toss a coin. */
export const SECTION_KEEP_MM = 1;

/** Every string this module hands to tr() through a variable (the i18n gate
 *  cannot see them): test/section3d.test.mjs checks each has its DICT entry. */
export const SECTION3D_STRINGS: readonly string[] = ['Height', 'X', 'Y', 'Offset', '3D section', 'Surface', 'XY', 'YZ', 'ZX'];

const dot = (a: Pt3, b: Pt3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const sub = (a: Pt3, b: Pt3): Pt3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const add = (a: Pt3, b: Pt3): Pt3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const mul = (a: Pt3, s: number): Pt3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
const cross = (a: Pt3, b: Pt3): Pt3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const unit = (a: Pt3): Pt3 => { const l = Math.hypot(a.x, a.y, a.z) || 1; return mul(a, 1 / l); };

/** A unit normal, snapped onto an axis it is within a hair of (a rotated
 *  object's face reads 1e-16 off it). */
export function snapNormal(n: Pt3): Pt3 {
  const u = unit(n);
  for (const a of Object.values(AXIS)) {
    const c = dot(u, a);
    if (Math.abs(c) > 1 - 1e-6) return mul(a, Math.sign(c));
  }
  return u;
}

/** The section's view direction. */
export function sectionNormal(s: Section3d): Pt3 {
  return { x: s.nx, y: s.ny, z: s.nz };
}

/** A point of the plane: the one it was laid through, moved by its offset. */
export function sectionPoint(s: Section3d): Pt3 {
  return { x: s.x + s.offset * s.nx, y: s.y + s.offset * s.ny, z: s.z + s.offset * s.nz };
}

/** How the section lies: on an orthogonal plane when its normal is that plane's
 *  axis, else parallel to a surface. */
export function sectionPlaneOf(s: Section3d): Section3dPlane {
  const n = sectionNormal(s);
  for (const k of ['xy', 'yz', 'zx'] as const) if (Math.abs(Math.abs(dot(n, AXIS[k])) - 1) < 1e-9) return k;
  return 'surface';
}

/** What the view keeps: `n · p + c ≥ 0` (drawing coordinates) — what lies beyond
 *  the plane along the view direction, and a face the plane lies on. */
export function sectionClip(s: Section3d): { n: Pt3; c: number } {
  const n = sectionNormal(s);
  return { n, c: SECTION_KEEP_MM - dot(n, sectionPoint(s)) };
}

/** Whether the view keeps a point. */
export function sectionKeeps(s: Section3d, p: Pt3): boolean {
  const { n, c } = sectionClip(s);
  return dot(n, p) + c >= 0;
}

/** A new section through `at` (the point under the pointer): parallel to the
 *  surface there (`face`, its normal), or on an orthogonal plane — viewed away
 *  from the eye, so it cuts away what stands between the eye and the plane. Null
 *  for a surface section with no surface. An orthogonal plane's figure is rounded
 *  to the whole mm. */
export function placeSection(plane: Section3dPlane, at: Pt3, face: Pt3 | null, eye: Pt3, id: string): Section3d | null {
  const n0 = plane === 'surface' ? (face ? snapNormal(face) : null) : AXIS[plane];
  if (!n0) return null;
  const toward = dot(n0, sub(at, eye));
  const n = mul(n0, toward > 0 ? 1 : toward < 0 ? -1 : plane === 'surface' ? -1 : 1);
  const s: Section3d = { id, x: at.x, y: at.y, z: at.z, nx: n.x + 0, ny: n.y + 0, nz: n.z + 0, offset: 0 };
  const k = sectionPlaneOf(s);
  if (k === 'xy') s.z = Math.round(s.z);
  else if (k === 'yz') s.x = Math.round(s.x);
  else if (k === 'zx') s.y = Math.round(s.y);
  return s;
}

/** Turn the section the other way: the plane stays, the view direction reverses. */
export function flipSection(s: Section3d): void {
  s.nx = -s.nx + 0; s.ny = -s.ny + 0; s.nz = -s.nz + 0;
  s.offset = -s.offset + 0;
}

/** Lay the section on an orthogonal plane through its point, viewed away from
 *  the eye. */
export function orientSection(s: Section3d, plane: OrthoPlane, eye: Pt3): void {
  Object.assign(s, placeSection(plane, sectionPoint(s), null, eye, s.id));
}

/** The figure the section's handle and panel row set: its height (XY), its X
 *  (YZ) or its Y (ZX); parallel to a surface, its offset along the view
 *  direction from the point it was laid through. `along` is where it grows. */
export function sectionFigure(s: Section3d): { label: string; value: number; along: Pt3 } {
  const p = sectionPoint(s), k = sectionPlaneOf(s);
  if (k === 'xy') return { label: 'Height', value: p.z, along: AXIS.xy };
  if (k === 'yz') return { label: 'X', value: p.x, along: AXIS.yz };
  if (k === 'zx') return { label: 'Y', value: p.y, along: AXIS.zx };
  return { label: 'Offset', value: s.offset, along: sectionNormal(s) };
}

/** Set the section's figure ({@link sectionFigure}): the plane moves along its normal. */
export function setSectionFigure(s: Section3d, v: number): void {
  const k = sectionPlaneOf(s);
  if (k === 'xy') s.offset = (v - s.z) / s.nz;
  else if (k === 'yz') s.offset = (v - s.x) / s.nx;
  else if (k === 'zx') s.offset = (v - s.y) / s.ny;
  else s.offset = v;
  s.offset += 0;
}

/** The section's handle: on its point, sliding the plane along its normal by its
 *  figure (the 3D view's handle machinery: arm, drag, a typed figure). */
export function section3dHandles(s: Section3d): Array<Handle<Section3d>> {
  const p = sectionPoint(s), f = sectionFigure(s);
  return [{
    key: 'section3d', kind: 'plane', z: p.z, points: [{ x: p.x, y: p.y }],
    height: { label: f.label, value: f.value, set: (o, v) => { setSectionFigure(o, v); return null; }, snaps: [], along: f.along },
  }];
}

/** An axis-aligned box (drawing coordinates). */
export interface Box3d { min: Pt3; max: Pt3 }

/** What the view keeps of a box (`n · p + c ≥ 0`), boxed: round its corners on
 *  the kept side and the points where its edges cross the plane — a selection's
 *  or a hover's box trimmed to what shows. Null when the plane cuts it all away.
 *  Any frame, the box's and the plane's one. */
export function trimBox(box: Box3d, n: Pt3, c: number): Box3d | null {
  const { min: lo, max: hi } = box;
  const corner = (i: number): Pt3 => ({ x: i & 1 ? hi.x : lo.x, y: i & 2 ? hi.y : lo.y, z: i & 4 ? hi.z : lo.z });
  const kept: Pt3[] = [];
  for (let i = 0; i < 8; i++) {
    const a = corner(i), da = dot(n, a) + c;
    if (da >= 0) kept.push(a);
    for (const bit of [1, 2, 4]) {
      if (i & bit) continue;
      const b = corner(i | bit), db = dot(n, b) + c;
      if ((da >= 0) !== (db >= 0)) kept.push(add(a, mul(sub(b, a), da / (da - db))));
    }
  }
  if (!kept.length) return null;
  const pick = (f: (...v: number[]) => number): Pt3 => ({ x: f(...kept.map((p) => p.x)), y: f(...kept.map((p) => p.y)), z: f(...kept.map((p) => p.z)) });
  return { min: pick(Math.min), max: pick(Math.max) };
}

/** The polygon where the plane through `p` square to `n` meets a box (the model's
 *  extents grown by `margin` each way), in order round it; a square about `p`
 *  where it misses the box, or with no box (`margin` its half side then). */
export function sectionOutline(p: Pt3, n: Pt3, box: Box3d | null, margin: number): Pt3[] {
  const nn = unit(n);
  const u = unit(cross(nn, Math.abs(nn.z) < 0.9 ? { x: 0, y: 0, z: 1 } : { x: 1, y: 0, z: 0 }));
  const v = cross(nn, u);
  const square = (h: number): Pt3[] => [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([a, b]) => add(p, add(mul(u, a * h), mul(v, b * h))));
  if (!box) return square(margin);
  const lo = sub(box.min, { x: margin, y: margin, z: margin }), hi = add(box.max, { x: margin, y: margin, z: margin });
  const corner = (i: number): Pt3 => ({ x: i & 1 ? hi.x : lo.x, y: i & 2 ? hi.y : lo.y, z: i & 4 ? hi.z : lo.z });
  const pts: Pt3[] = [];
  for (let i = 0; i < 8; i++) {
    for (const bit of [1, 2, 4]) {
      if (i & bit) continue;
      const a = corner(i), b = corner(i | bit);
      const da = dot(sub(a, p), nn), db = dot(sub(b, p), nn);
      if ((da > 0 && db > 0) || (da < 0 && db < 0) || da === db) continue;
      const q = add(a, mul(sub(b, a), da / (da - db)));
      if (!pts.some((o) => Math.hypot(o.x - q.x, o.y - q.y, o.z - q.z) < 1e-6)) pts.push(q);
    }
  }
  if (pts.length < 3) return square(Math.max(hi.x - lo.x, hi.y - lo.y, hi.z - lo.z) / 2);
  const c = mul(pts.reduce(add, { x: 0, y: 0, z: 0 }), 1 / pts.length);
  return pts.sort((a, b) => Math.atan2(dot(sub(a, c), v), dot(sub(a, c), u)) - Math.atan2(dot(sub(b, c), v), dot(sub(b, c), u)));
}
